import { toast } from "@carbon6/c6-frontend-design";
import type { DatasetUploadJob } from "@/api";
import { windowStack, windowTaskbarTextLabel } from "@/app/windowStack";
import type { WindowStackManager } from "@/components/modal";

/**
 * 앱 스코프 업로드 창 컨트롤러(facade).
 *
 * Owns:
 * - 업로드 세션(설정, 진행, 완료 검토)을 전체화면 창 스택의 "datasetUpload" kind 엔트리에 대응시킨다.
 *   창은 업로드 1건당 하나다(key 는 클라이언트가 만든 uuid). 그래서 여러 업로드를 동시에 열 수 있다.
 * - 한 브라우저의 동시 업로드 창 상한(기본 3, 소비처가 open 에서 재정의). 상한을 넘으면 toast 로 안내한다.
 * - setup→active 전환. 진행 단계로 넘어가면 닫기를 금지한다(closable:false). terminal 이면 닫기를 허용한다.
 *
 * Does not own:
 * - 업로드 실행·진행(uploadEngine), 설정 폼 상태(UploadWindowController), 콘텐츠 렌더(UploadWindowContent).
 * - 전역 창 stacking 정책(WindowStackManager). 업로드 창은 bypassCap 으로 전역 상한을 우회한다.
 *   대신 자체 상한(maxConcurrent)을 여기서 적용한다.
 */

export const UPLOAD_WINDOW_KIND = "datasetUpload";

// 한 브라우저에서 동시에 열 수 있는 기본 업로드 창 수. 소비처(진입점)가 open 에서 재정의할 수 있다.
export const DEFAULT_MAX_CONCURRENT_UPLOADS = 3;

// 설정 단계를 재개·재업로드 대상 잡으로 미리 채우는 seed. 업로드 목록의 행 액션이 전달한다.
export type UploadSeed = { kind: "resume" | "retry"; job: DatasetUploadJob };

// 창 콘텐츠(UploadWindowContent)가 읽는 kind별 payload 계약.
export type UploadWindowParams =
  | {
      phase: "setup";
      // 재개·재업로드 대상. 없으면 신규 폼이다.
      seed: UploadSeed | null;
    }
  | {
      phase: "active";
      // 진행·완료를 구독할 엔진 잡 id. 새로고침으로 엔진이 비면 콘텐츠가 만료 안내를 표시한다.
      engineJobId: string;
      displayName: string;
    };

export type OpenUploadWindowInput = {
  // 재개/재업로드 대상으로 설정 단계를 미리 채운다.
  seed?: UploadSeed;
  // 동시 업로드 창 상한(기본 DEFAULT_MAX_CONCURRENT_UPLOADS). 넘으면 열지 않고 toast 를 표시한다.
  maxConcurrent?: number;
  // 창이 닫힐 때(완료·폐기 후) 1회 호출한다. 목록 새로고침 같은 정리에 쓴다.
  onClose?: () => void;
};

function uploadEntries(stack: WindowStackManager): readonly { id: string }[] {
  return stack.getSnapshot().entries.filter((entry) => entry.kind === UPLOAD_WINDOW_KIND);
}

export class UploadWindowsController {
  private seq = 0;

  constructor(private readonly stack: WindowStackManager = windowStack) {}

  // 새 업로드 설정 창을 연다. 자체 상한을 넘으면 toast 로 안내하고 열지 않는다.
  open = (input: OpenUploadWindowInput = {}): void => {
    const max = input.maxConcurrent ?? DEFAULT_MAX_CONCURRENT_UPLOADS;
    if (uploadEntries(this.stack).length >= max) {
      toast.info(
        `업로드는 한 번에 최대 ${max}건까지 열 수 있습니다. 진행 중인 업로드를 마친 뒤 다시 시도해 주세요.`,
      );
      return;
    }
    const seed = input.seed ?? null;
    const title = titleForSetup(seed);
    this.stack.open({
      kind: UPLOAD_WINDOW_KIND,
      key: `upload-${(this.seq += 1)}-${randomKey()}`,
      title,
      label: windowTaskbarTextLabel("업로드:", title),
      // 설정 단계에는 아직 잡이 없으므로 언제든 닫을 수 있다. active 로 전환하면 닫기를 잠근다.
      closable: true,
      // 전역 상한을 우회한다. 실제 제한은 자체 상한(max)이 한다. 진행 중 창은 반드시 열려 있어야 한다.
      bypassCap: true,
      params: { phase: "setup", seed } satisfies UploadWindowParams,
      onClose: input.onClose,
    });
  };

  // 테이블 행의 "이어서 업로드". 그 서버 잡을 재개 대상으로 설정 창을 연다.
  openResume = (job: DatasetUploadJob, onClose?: () => void): void => {
    this.open({ seed: { kind: "resume", job }, onClose });
  };

  // 테이블 행의 "재시도(재업로드)". 그 서버 잡을 reset 대상으로 설정 창을 연다.
  openRetry = (job: DatasetUploadJob, onClose?: () => void): void => {
    this.open({ seed: { kind: "retry", job }, onClose });
  };

  // 설정을 제출한 뒤 창을 진행 단계로 전환하고 닫기를 잠근다. 완료나 폐기 전에는 닫을 수 없다.
  toActive = (windowId: string, engineJobId: string, displayName: string): void => {
    this.stack.update(windowId, {
      title: displayName,
      label: windowTaskbarTextLabel("업로드:", displayName),
      closable: false,
      params: { phase: "active", engineJobId, displayName } satisfies UploadWindowParams,
    });
  };

  // 진행 상태 라벨(작업표시줄 eyebrow)을 갱신한다. 잡 상태가 바뀔 때 콘텐츠가 호출한다.
  setSubtitle = (windowId: string, subtitle: string): void => {
    this.stack.update(windowId, { subtitle });
  };

  // terminal(완료·취소)로 전환되면 닫기를 허용한다. 콘텐츠가 잡 상태를 보고 호출한다.
  setClosable = (windowId: string, closable: boolean): void => {
    this.stack.update(windowId, { closable });
  };

  // 완료·폐기 확정. 닫기를 허용한 뒤 창을 닫는다. 창이 닫히면 onClose 가 목록을 새로고침한다.
  close = (windowId: string): void => {
    this.stack.update(windowId, { closable: true });
    this.stack.close(windowId);
  };

  // 최소화된 창을 복원한다. 작업표시줄 클릭이 아닌 다른 경로에서 쓴다.
  restore = (windowId: string): void => {
    this.stack.setActive(windowId);
  };
}

function titleForSetup(seed: UploadSeed | null): string {
  if (seed !== null) {
    return seed.kind === "resume" ? "업로드 이어받기" : "업로드 재시도";
  }
  return "Dataset 업로드";
}

// 창 key 는 업로드마다 유일하기만 하면 된다(dedup 대상 아님). crypto.randomUUID 가 없으면 시퀀스를 쓴다.
function randomKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? "k";
}

// 앱 스코프 싱글턴. 모듈은 한 번만 평가되므로 StrictMode 재마운트에서도 같은 인스턴스를 유지한다.
export const uploadWindows = new UploadWindowsController();
